import { FC, useCallback, useEffect } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { MODAL_IDS, ModalItem, ModalSimple, useModal } from 'components'
import {
  useAttachCustomerByContactIdMutation,
  useDeleteContactMutation,
  useDetachCustomerByContactIdMutation,
  useGetContactListQuery,
  useGetCustomersByContactIdQuery,
  useLazyGetContactByIdQuery
} from 'modules/contact/api'
import { ContactCustomersTable } from 'modules/contact/components'
import { getContactFullName, getContactFullPhone } from 'modules/contact/helpers'
import { contactDetailUpdateRoute, contactRoute } from 'modules/contact/routes'
import { useGetCustomerListQuery } from 'modules/customer/api'
import { Button } from 'src/shared/components'
import { PlaceholderCell } from 'src/tailwind-components/extends/PlaceholderCell'

const ContactDetailContent: FC<{ id?: string }> = ({ id }) => {
  const navigate = useNavigate()
  const {
    location: { pathname }
  } = useRouterState()
  const [getContactById, { data, isLoading, isFetching }] = useLazyGetContactByIdQuery()
  const {
    data: attachedCustomers,
    isLoading: isAttachedCustomersLoading,
    isFetching: isAttachedCustomersFetching,
    refetch: refetchAttachedCustomers
  } = useGetCustomersByContactIdQuery(id || '')
  const {
    data: customers,
    isLoading: isCustomerListLoading,
    isFetching: isCustomerListFetching,
    refetch: refetchCustomers
  } = useGetCustomerListQuery()
  const [attachCustomerByContactId] = useAttachCustomerByContactIdMutation()
  const [detachCustomerByContactId] = useDetachCustomerByContactIdMutation()

  const { refetch } = useGetContactListQuery('')
  const [deleteContactDetail, { isLoading: isDeleting }] = useDeleteContactMutation()

  const title = 'контакт'
  const subtitle = 'контактом'

  useEffect(() => {
    refetchAttachedCustomers()
  }, [attachedCustomers]) // eslint-disable-line

  useEffect(() => {
    getContactById(id || '')
  }, []) // eslint-disable-line

  const { openModal, closeModal } = useModal()

  const onClose = () => {
    closeModal(MODAL_IDS.ContactDetail)
    navigate(contactRoute)
  }

  const handleDelete = useCallback(() => {
    if (data) {
      deleteContactDetail(data.id).finally(() => {
        closeModal(MODAL_IDS.DeleteContactDetail)
        closeModal(MODAL_IDS.ContactDetail)
        refetch()
        navigate(contactRoute)
      })
    }
  }, [closeModal, deleteContactDetail, data, navigate, refetch])

  const handleOpenModalDelete = useCallback(() => {
    openModal(MODAL_IDS.DeleteContactDetail)
  }, [openModal])

  const handleUpdateContact = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    const id = data?.id
    if (!id) {
      return
    }
    navigate({ to: contactDetailUpdateRoute.to, params: { id }, search: { from: pathname, skip: true } })
  }

  return (
    <div>
      <div className="flex gap-4 pb-2 text-body-md">
        <div className="w-full max-w-[152px] text-[#6e7076]">Телефон</div>
        <div className="w-full text-[#1a1c1e]" data-test-id="contacts-page--contact-detail--full-phone">
          {isFetching ? (
            <PlaceholderCell className="max-w-[200px]" height={20} />
          ) : (
            (data && getContactFullPhone(data)) || '—'
          )}
        </div>
      </div>
      {
        /* eslint-disable-next-line */
        data?.access?.viewCustomer &&
          (isAttachedCustomersLoading ? (
            <PlaceholderCell height={120} />
          ) : (
            <ContactCustomersTable
              attachCustomerByContactId={attachCustomerByContactId}
              attachedCustomers={attachedCustomers}
              contactId={id || ''}
              customers={customers}
              detachCustomerByContactId={detachCustomerByContactId}
              isAccessAddCustomer={data.access.addCustomer}
              isAccessDeleteCustomer={data.access.deleteCustomer}
              isAttachedCustomersFetching={isAttachedCustomersFetching}
              isAttachedCustomersLoading={isAttachedCustomersLoading}
              isCustomerListFetching={isCustomerListFetching}
              isCustomerListLoading={isCustomerListLoading}
              refetchAttachedCustomers={refetchAttachedCustomers}
              refetchCustomers={refetchCustomers}
            />
          ))
      }
      <div className="flex justify-between px-0 py-4">
        <Button
          disabled={!data?.access?.delete}
          loading={isLoading}
          onClick={handleOpenModalDelete}
          size="lg"
          data-test-id="contacts-page--contact-detail--delete-btn"
        >
          Удалить
        </Button>
        <div className="flex gap-x-2">
          <Button
            loading={isLoading}
            onClick={handleUpdateContact}
            size="lg"
            data-test-id="contacts-page--contact-detail--update-btn"
          >
            Редактировать
          </Button>
          <Button loading={isLoading} onClick={onClose} size="lg" variant="solid">
            Готово
          </Button>
        </div>
      </div>

      <ModalItem id={MODAL_IDS.DeleteContactDetail}>
        <ModalSimple
          id={MODAL_IDS.DeleteContactDetail}
          onClose={() => closeModal(MODAL_IDS.DeleteContactDetail)}
          title={`Удалить ${title} «${data ? getContactFullName(data) : '-'}»?`}
          mobileModalFooter={
            <Button
              disabled={!data?.access.delete}
              loading={isDeleting}
              onClick={handleDelete}
              size="lg"
              variant="solid"
            >
              Удалить
            </Button>
          }
          modalFooter={
            <div className="ml-auto flex gap-x-2">
              <Button
                className="ml-auto"
                onClick={() => closeModal(MODAL_IDS.DeleteContactDetail)}
                size="lg"
                variant="outline"
              >
                Не удалять
              </Button>
              <Button
                disabled={!data?.access.delete}
                loading={isDeleting}
                onClick={handleDelete}
                size="lg"
                variant="solid"
                data-test-id="contacts-page--contact-delete--delete-btn"
              >
                Удалить
              </Button>
            </div>
          }
        >
          {`В заявках, которые были ранее созданы с этим ${subtitle}, информация о нём сохранится.`}
        </ModalSimple>
      </ModalItem>
    </div>
  )
}

export default ContactDetailContent
